/** @format */

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useUpdatePassword } from '../hooks/useUpdatePassword';
import { PasswordRequirements } from './PasswordRequirements';
import Key from 'img/Key.svg';

/**
 * UpdatePasswordForm Component
 * Form for updating password during NEW_PASSWORD_REQUIRED challenge
 */
export const UpdatePasswordForm: React.FC = () => {
  const navigate = useNavigate();
  const {
    newPassword,
    confirmPassword,
    loading,
    error,
    setNewPassword,
    setConfirmPassword,
    submitPassword,
    clearError,
    hasValidSession,
  } = useUpdatePassword();

  // Redirect if no valid session
  useEffect(() => {
    if (!hasValidSession && error) {
      toast.error(error);
      const timer = setTimeout(() => {
        navigate('/signin');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [hasValidSession, error, navigate]);

  // Show error toast when error changes
  useEffect(() => {
    if (error && hasValidSession) {
      toast.error(error);
      clearError();
    }
  }, [error, clearError, hasValidSession]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await submitPassword();
    if (success) {
      toast.success('Password updated successfully');
    }
  };

  if (!hasValidSession) {
    return (
      <div className='update-formcontainer'>
        <div className='update-password-form'>
          <img src={Key} alt='key illustration' />
          <h2>Session Expired</h2>
          <p>Redirecting to sign in...</p>
        </div>
      </div>
    );
  }

  return (
    <div className='update-formcontainer'>
      <div className='update-password-form'>
        <img src={Key} alt='key illustration' />
        <h2>Setup Password</h2>
        <form style={{ width: '60%' }} onSubmit={handleSubmit}>
          <input
            id='newPassword'
            type='password'
            placeholder='New Password'
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            disabled={loading}
          />

          <PasswordRequirements password={newPassword} />

          <input
            id='confirmPassword'
            type='password'
            placeholder='Confirm Password'
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            disabled={loading}
          />

          {confirmPassword && newPassword !== confirmPassword && (
            <p style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
              Passwords do not match
            </p>
          )}

          <div>
            <button type='submit' disabled={loading}>
              {loading ? 'Updating...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdatePasswordForm;
